import { useEffect, useMemo, useRef } from 'react';
import { useLiving } from '../../contexts/LivingContext';
import { useI18n } from '../../i18n';
import { registerParallax, seeded, clamp } from '../../lib/living';
import { burst } from '../../lib/fx';
import { wmoLabel } from '../../lib/utils';
import LivingEmoji from './LivingEmoji';
import CloudShape from './CloudShape';
import './LivingWeather.css';

const SYNODIC = 29.530588853;
const KNOWN_NEW_MOON = Date.UTC(2000, 0, 6, 18, 14);

/** Offset of the moon's shadow disc: covers it at new moon, clears it at full. */
function moonShadow(now) {
    const age = (((now - KNOWN_NEW_MOON) / 86400000) % SYNODIC + SYNODIC) % SYNODIC;
    const half = SYNODIC / 2;
    return age < half ? (age / half) * 110 : -110 + ((age - half) / half) * 110;
}

const CLOUDS = { clear: 1, partly: 3, cloudy: 5, fog: 4, rain: 5, storm: 6, snow: 4 };

/* A living window onto the sky above the user's garden right now. */
export default function SkyScene({ plants = [] }) {
    const living = useLiving();
    const { t, locale } = useI18n();
    const ref = useRef(null);
    const { elevation, phase, mood, wind, temp, season, hourAngle, wx, clock, motion } = living;

    useEffect(() => motion === 'full' ? registerParallax(ref.current) : undefined, [motion]);

    const stars = useMemo(() => {
        const r = seeded('growdaily-stars');
        return Array.from({ length: 28 }, () => ({ left: r() * 100, top: r() * 60, delay: r() * 3, size: 1 + r() * 2 }));
    }, []);

    // Real cloud cover (0–100 %) decides how many clouds drift past
    const cloudCount = Number.isFinite(wx.cloud) ? Math.round(clamp(wx.cloud / 100) * 7) : (CLOUDS[mood] || 2);
    const clouds = useMemo(() => {
        const r = seeded(`clouds-${cloudCount}`);
        return Array.from({ length: cloudCount }, (_, i) => ({
            top: 8 + r() * 38,
            left: r() * 85,
            width: 60 + r() * 70,
            delay: -r() * 60,
            speed: 0.7 + r() * 0.6,
            key: i,
        }));
    }, [cloudCount]);

    const starOpacity = clamp((-elevation - 3) / 10);
    const cloudDuration = Math.max(12, 90 - wind * 75); // real wind (and gusts) speed the clouds up
    const darkClouds = wx.precip !== 'none' || wx.thunder || (wx.cloud ?? 0) > 85;
    const sunDim = 1 - Math.min(0.8, Math.max(0, ((wx.cloud ?? 0) - 55) / 55)) - (wx.precip !== 'none' ? 0.15 : 0);
    const snowy = wx.precip === 'snow' || wx.precip === 'grains' || (wx.icy && (temp ?? 5) <= 0);
    const phaseKey = phase === 'night' && clock === 'late' ? 'late'
        : (phase === 'dawn' || phase === 'morning') && clock === 'early' ? 'early'
            : phase === 'night' && clock === 'evening' ? 'evening' : phase;
    const wxLabel = wx.code != null ? wmoLabel(wx.precip === 'sleet' ? 'sleet' : wx.code, locale) : null;
    const garden = plants.length ? plants.slice(0, 6).map((p) => p.emoji || '🌱') : ['🌱', '🌷', '🌿'];
    const showMoon = elevation < -2;

    const celebrate = (e) => {
        burst({ x: e.clientX, y: e.clientY }, showMoon ? 'sparkle' : 'leaves');
    };

    return (
        <div
            ref={ref}
            data-testid="sky-scene"
            onClick={celebrate}
            className="sky-scene h-44 sm:h-52 cursor-pointer select-none"
            style={{ '--storm-period': `${motion === 'calm' ? 28 : season === 'spring' || season === 'autumn' ? 13 : 20}s` }}
            role="img"
            aria-label={`${t(`live.phase.${phase}`)} · ${wxLabel ? wxLabel.label : ''} · ${t(`live.season.${season}`)}`}
        >
            {starOpacity > 0 && stars.map((s, i) => (
                <span
                    key={i}
                    className="sky-star"
                    style={{ left: `${s.left}%`, top: `${s.top}%`, width: s.size, height: s.size, animationDelay: `${s.delay}s`, opacity: starOpacity }}
                />
            ))}

            <div
                className="sky-scene__celestial"
                // The moon rides roughly opposite the sun (hour angle + 180°)
                style={showMoon ? {
                    left: `${clamp(50 + ((((hourAngle + 360) % 360) - 180) / 90) * 40, 12, 88)}%`,
                    top: 'max(16%, calc(100% - var(--sun-y)))',
                } : undefined}
            >
                {showMoon
                    ? <div className="sky-scene__moon" style={{ '--moon-shadow': `${moonShadow(Date.now())}%` }} />
                    : <div className="sky-scene__sun" style={{ opacity: Math.max(0.2, sunDim) }} />}
            </div>

            {clouds.map((c) => (
                <span
                    key={c.key}
                    className={`sky-cloud ${darkClouds ? 'sky-cloud--dark' : ''}`}
                    style={{ left: `${c.left}%`, top: `${c.top}%`, width: c.width, animationDuration: `${cloudDuration * c.speed}s`, animationDelay: `${c.delay}s` }}
                ><CloudShape /></span>
            ))}

            {wx.fog && <div className="sky-fog" />}
            {wx.precip !== 'none' && (
                <div className={`sky-precip sky-precip--${wx.precip}`} style={{ '--precip': wx.intensity, '--slant': `${Math.round(wind * 30)}deg` }} />
            )}
            {wx.hail && <div className="sky-precip sky-precip--hail" />}
            {wx.thunder && motion !== 'off' && (
                <svg className="sky-bolt" viewBox="0 0 40 100" aria-hidden>
                    <path d="M24 0 L8 46 L20 46 L10 100 L34 38 L21 38 L30 0Z" />
                </svg>
            )}

            <div className={`sky-hills ${snowy ? 'is-snowy' : ''} ${wx.icy ? 'is-icy' : ''}`} aria-hidden>
                <svg viewBox="0 0 400 90" preserveAspectRatio="none" className="sky-hill-back absolute bottom-6 left-0 right-0" style={{ height: 70 }}>
                    <path d="M0 60 Q 60 20 130 45 T 260 35 T 400 40 V 90 H 0 Z" style={{ fill: 'color-mix(in srgb, var(--brand-sage) 55%, var(--sky-bottom))' }} />
                </svg>
                <svg viewBox="0 0 400 90" preserveAspectRatio="none" className="sky-hill-mid absolute bottom-2 left-0 right-0" style={{ height: 60 }}>
                    <path d="M0 55 Q 90 25 180 50 T 400 45 V 90 H 0 Z" style={{ fill: 'color-mix(in srgb, var(--brand-green) 70%, var(--brand-sage))' }} />
                </svg>
                <svg viewBox="0 0 400 90" preserveAspectRatio="none" className="sky-hill-front relative" style={{ height: 44, width: '100%' }}>
                    <path d="M0 40 Q 100 15 200 32 T 400 28 V 90 H 0 Z" style={{ fill: 'var(--brand-greenDark)' }} />
                </svg>
            </div>

            <div className="sky-garden">
                {garden.map((emoji, i) => (
                    <LivingEmoji key={i} seed={`${emoji}-${i}`} className="text-2xl drop-shadow">{emoji}</LivingEmoji>
                ))}
            </div>

            <div className="absolute top-3 left-3 max-w-[calc(100%-5.5rem)] inline-flex items-center gap-1.5 rounded-full bg-black/20 backdrop-blur-md px-3 py-1 text-[10px] uppercase tracking-widest font-semibold text-white whitespace-nowrap overflow-hidden">
                <span className="w-1.5 h-1.5 shrink-0 rounded-full bg-emerald-300 living-pulse" />
                <span className="truncate">{t(`live.phase.${phaseKey}`)} · {wxLabel ? `${wxLabel.icon} ${wxLabel.label}` : t(`live.season.${season}`)}
                {wx.gale ? ' · 💨' : wx.windy ? ' · 🍃' : ''}</span>
            </div>
            {temp != null && (
                <div className="absolute top-3 right-3 rounded-full bg-black/20 backdrop-blur-md px-3 py-1 text-sm font-serif text-white">
                    {Math.round(temp)}°
                </div>
            )}
        </div>
    );
}
